/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction } from "redux"
import { notification } from "@duxton-web/ui"
import { actions } from "store/duck/bulkUpload/actions"
import { AppState } from "store/store.type"
import { AxiosAction } from "types/api"
import { useTranslation } from "common/utils/i18n"
import {
  ACTION_TYPE,
  ExportMenuItemProperty,
  InventoryExportedFileResponse,
} from "types/delv-platform-api/inventoryCategory"
import { BULK_UPLOAD_TYPE } from "pages/menu/detail/bulkUpload/BulkUploadContext"

/**
 * Standalone "Download BCRS suggestions" trigger for nudge banners shown
 * outside the bulk-upload drawer (e.g. the menu overview). Exports the whole
 * menu (all categories + all columns) with the BCRS column(s) appended; the
 * backend returns only the BCRS-suggested items when includeBcrsColumn is set.
 */
export const useDownloadBcrsSuggestions = () => {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState(false)
  const exportFailedText = useTranslation(
    "bulkUpload.notifications.exportFileFailed.title",
  )
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    categories,
  } = useSelector((state: AppState) => state.menu)

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      const res = (await dispatch(
        actions.fetchInventoryExportedFile(
          {
            categoryIDs: categories
              ?.map(category => category.categoryID)
              .filter((id): id is string => !!id),
            menuGroupID,
            properties: Object.values(ExportMenuItemProperty),
            templateType: ACTION_TYPE.UPDATE,
            includeBcrsColumn: true,
          },
          merchantID,
          merchantGroupID,
          BULK_UPLOAD_TYPE.MENU,
        ) as AnyAction,
      )) as AxiosAction<InventoryExportedFileResponse>

      // redux-observable resolves the promise with res.error = true on a
      // 4xx/5xx rather than throwing, so try/catch won't see it — surface it.
      if (res?.error) {
        notification.error({ message: exportFailedText })
        return
      }

      const downloadUrl = res?.payload?.data?.downloadUrl
      if (downloadUrl) {
        // Only follow http(s) URLs — guard against a javascript:/data: or
        // otherwise unexpected scheme in the response (redirect sink).
        const parsedUrl = new URL(downloadUrl, window.location.origin)
        if (parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:") {
          window.location.assign(parsedUrl.href)
        }
      }
    } finally {
      setIsDownloading(false)
    }
  }

  return { onDownload, isDownloading }
}
